import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { money } from "../lib/api";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { toast } from "sonner";
import {
  AlertCircle, CheckCircle2, Clock, Camera, Hourglass, ShieldCheck,
  TrendingDown, Send, X, Plus, RefreshCw, Package, DollarSign, Image as ImageIcon, Video,
} from "lucide-react";
import StepUpModal from "./StepUpModal";

// ─────────────────────────────────────────────────────────────────────────────
// ClaimsTab — shared table+drawer used inside the Orders page on both
// buyer (/buyer/orders#claims) and seller (/seller/orders#claims) sides.
// API surface is symmetric (/api/claims, /api/claims/{id}/reply) so the same
// component works regardless of role. The role prop changes copy + which CTAs
// are surfaced (e.g. only buyers see "Open new claim" if there are eligible
// delivered orders).
// ─────────────────────────────────────────────────────────────────────────────
const STATE_META = {
  open:                    { label: "Open · awaiting seller", chip: "bg-amber-100 text-amber-800" },
  seller_replied:          { label: "Seller replied",         chip: "bg-blue-100 text-blue-800" },
  buyer_replied:           { label: "Buyer replied",          chip: "bg-blue-100 text-blue-800" },
  settled:                 { label: "Settled",                chip: "bg-emerald-100 text-emerald-800" },
  auto_closed_no_proof:    { label: "Auto-closed · no proof", chip: "bg-foreground/10 text-foreground/60" },
  auto_forwarded_allianz:  { label: "Forwarded to Allianz",   chip: "bg-emerald-100 text-emerald-800" },
};

const REASON_LABEL = {
  damaged:      "Damaged in transit",
  wrong_item:   "Wrong item received",
  quality:      "Quality below spec",
  qty_mismatch: "Quantity mismatch",
  expired:      "Expired / near-expiry",
  other:        "Other",
};

function timeRemaining(deadlineIso) {
  if (!deadlineIso) return null;
  const ms = new Date(deadlineIso).getTime() - Date.now();
  if (ms <= 0) return { expired: true, label: "Expired" };
  const days = Math.floor(ms / 86400000);
  const hrs = Math.floor((ms % 86400000) / 3600000);
  if (days > 0) return { expired: false, label: `${days}d ${hrs}h`, urgent: days < 1 };
  return { expired: false, label: `${hrs}h`, urgent: true };
}

export default function ClaimsTab({ role, orders }) {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [openingForOrder, setOpeningForOrder] = useState(null); // order obj when buyer clicks "Open new claim"
  const location = useLocation();
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/claims")
      .then(r => setClaims(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Deep-link from the buyer's order drawer: clicking "File a claim" on a
  // delivered order navigates here with `?new_claim=ORDER_ID#claims`. We
  // pop the new-claim modal pre-selected to that order and then strip the
  // query param so a refresh doesn't re-open it.
  useEffect(() => {
    if (role !== "buyer") return;
    const params = new URLSearchParams(location.search);
    const targetOid = params.get("new_claim");
    if (!targetOid || !orders?.length) return;
    const order = orders.find(o => o.id === targetOid);
    if (order && order.status === "delivered") {
      setOpeningForOrder(order);
    } else if (order) {
      toast.info("Claims can only be opened on delivered orders.");
    } else {
      toast.error("Order not found.");
    }
    // Strip the param so navigation history stays clean.
    params.delete("new_claim");
    const qs = params.toString();
    navigate(
      `${location.pathname}${qs ? `?${qs}` : ""}${location.hash}`,
      { replace: true }
    );
  }, [role, orders, location.search, location.pathname, location.hash, navigate]);

  const active = useMemo(() => claims.find(c => c.id === activeId), [claims, activeId]);

  // Buyers can open a claim against any delivered order they own that
  // doesn't already have an active claim.
  const eligibleOrders = useMemo(() => {
    if (role !== "buyer") return [];
    const claimedOids = new Set(
      claims
        .filter(c => !["settled", "auto_closed_no_proof", "auto_forwarded_allianz"].includes(c.status))
        .map(c => c.order_id)
    );
    return (orders || []).filter(o => o.status === "delivered" && !claimedOids.has(o.id));
  }, [role, orders, claims]);

  const counts = useMemo(() => ({
    open: claims.filter(c => ["open", "seller_replied", "buyer_replied"].includes(c.status)).length,
    settled: claims.filter(c => c.status === "settled").length,
    forwarded: claims.filter(c => c.status === "auto_forwarded_allianz").length,
    no_proof: claims.filter(c => c.status === "auto_closed_no_proof").length,
  }), [claims]);

  return (
    <div className="space-y-4" data-testid="claims-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { l: "Open claims", v: counts.open, sub: "awaiting reply" },
          { l: "Settled", v: counts.settled, sub: "bilaterally" },
          { l: "Forwarded · Allianz", v: counts.forwarded, sub: "in insurer pipeline" },
          { l: "Auto-closed · no proof", v: counts.no_proof, sub: role === "buyer" ? "score impact applied" : "buyer score dropped" },
        ].map((t, i) => (
          <div key={i} className="rounded-2xl border border-border bg-white p-5">
            <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{t.l}</div>
            <div className="display text-2xl mt-2 tracking-tight">{t.v}</div>
            <div className="text-[11px] text-foreground/55 mt-0.5">{t.sub}</div>
          </div>
        ))}
      </div>

      {/* Buyer · Open new claim */}
      {role === "buyer" && eligibleOrders.length > 0 && (
        <div className="rounded-2xl border-2 border-dashed border-foreground/20 bg-foreground/[0.02] p-5 flex items-center gap-4">
          <div className="flex-1">
            <div className="text-sm font-medium">Got an issue with a delivered order?</div>
            <div className="text-xs text-foreground/60 mt-0.5">
              {eligibleOrders.length} delivered order{eligibleOrders.length === 1 ? "" : "s"} eligible · QI window applies (default 7 days post-delivery).
            </div>
          </div>
          <Button
            onClick={() => setOpeningForOrder(eligibleOrders[0])}
            data-testid="claim-open-new-btn"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-4"
          >
            <Plus className="w-4 h-4 mr-1.5" />Open new claim
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl bg-white border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left text-xs uppercase tracking-widest text-foreground/60">
            <tr>
              <th className="px-5 py-3">Claim</th>
              <th className="px-5 py-3">Order</th>
              <th className="px-5 py-3">{role === "buyer" ? "Brand" : "Buyer"}</th>
              <th className="px-5 py-3">Reason</th>
              <th className="px-5 py-3">Amount</th>
              <th className="px-5 py-3">Deadline</th>
              <th className="px-5 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && (
              <tr><td colSpan={7} className="text-center py-12 text-foreground/55">Loading claims…</td></tr>
            )}
            {!loading && claims.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-16 text-foreground/55 text-sm" data-testid="claims-empty">
                  <div className="display text-base">No claims yet</div>
                  <div className="text-xs mt-1 text-foreground/50">
                    {role === "buyer"
                      ? "If a delivered order arrives damaged, short, or wrong, you can open a claim within the seller's QI window."
                      : "Claims your buyers open will appear here. Respond before the deadline to avoid Allianz auto-forward (if covered)."}
                  </div>
                </td>
              </tr>
            )}
            {!loading && claims.map(c => {
              const meta = STATE_META[c.status] || STATE_META.open;
              const remain = timeRemaining(c.deadline_at);
              const isActive = ["open", "seller_replied", "buyer_replied"].includes(c.status);
              return (
                <tr
                  key={c.id}
                  className="hover:bg-secondary/30 cursor-pointer"
                  data-testid={`claim-row-${c.code}`}
                  onClick={() => setActiveId(c.id)}
                >
                  <td className="px-5 py-4 mono text-foreground/70">{c.code}</td>
                  <td className="px-5 py-4 mono text-foreground/70">{c.order_code || "—"}</td>
                  <td className="px-5 py-4">{role === "buyer" ? c.seller_name : c.buyer_name}</td>
                  <td className="px-5 py-4 text-foreground/75">{REASON_LABEL[c.reason] || c.reason}</td>
                  <td className="px-5 py-4 display">{money(c.requested_amount || 0)}</td>
                  <td className="px-5 py-4 text-xs">
                    {!isActive ? <span className="text-foreground/40">—</span>
                     : remain?.expired ? <span className="text-foreground/55">Expired</span>
                     : <span className={remain?.urgent ? "text-rose-700 font-medium" : "text-foreground/70"}>{remain?.label} left</span>}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`chip ${meta.chip}`}>{meta.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      <Sheet open={!!activeId} onOpenChange={() => setActiveId(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="display text-2xl">Claim {active?.code}</SheetTitle>
          </SheetHeader>
          {active && <ClaimDrawer claim={active} role={role} onChange={(updated) => {
            setClaims(cs => cs.map(c => c.id === updated.id ? updated : c));
          }} />}
        </SheetContent>
      </Sheet>

      {/* Open-new-claim drawer (buyer only) */}
      <Sheet open={!!openingForOrder} onOpenChange={(o) => !o && setOpeningForOrder(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="display text-2xl">Open a claim</SheetTitle>
          </SheetHeader>
          {openingForOrder && (
            <OpenClaimForm
              order={openingForOrder}
              eligibleOrders={eligibleOrders}
              onCancel={() => setOpeningForOrder(null)}
              onSubmitted={(claim) => {
                setOpeningForOrder(null);
                setClaims(cs => [claim, ...cs]);
                setActiveId(claim.id);
                toast.success(`Claim ${claim.code} opened`);
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── Drawer: bilateral timeline + reply form ────────────────────────────────
function ClaimDrawer({ claim, role, onChange }) {
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ note: "", counter_outcome: "", counter_amount: "", shipping_payer: "buyer", shipping_fee: "" });
  const [pending, setPending] = useState([]); // {path, content_type, name}
  const [stepUp, setStepUp] = useState(null); // { amount } when we need to pop SMS
  const remain = timeRemaining(claim.deadline_at);
  const isActive = ["open", "seller_replied", "buyer_replied"].includes(claim.status);
  const youAre = role; // 'buyer' | 'seller'

  // Amount we'd settle on right now — used to drive the step-up SMS
  // prompt copy. When current_offer exists (it always does for an open
  // claim), trust ONLY current_offer — falling back to requested_* would
  // mis-represent a replacement counter-offer as a refund.
  const currentOfferAmount = Number(claim?.current_offer?.amount || 0);
  const currentOfferOutcome = claim?.current_offer?.outcome || null;
  const currentShippingPayer = claim?.current_offer?.shipping_payer || null;
  const currentShippingFee = Number(claim?.current_offer?.shipping_fee || 0);
  const sellerSettlingRefund = (
    role === "seller" &&
    ["refund", "partial_credit"].includes(currentOfferOutcome) &&
    currentOfferAmount > 0
  );
  const settlingReplacement = currentOfferOutcome === "replacement";
  const offerByMyRole = claim?.current_offer?.by_role === role;

  const reply = async (turn, stepUpToken = null) => {
    setBusy(true);
    try {
      const payload = {
        turn,
        note: draft.note,
        attachment_paths: pending.map(p => p.path),
      };
      if (turn === "counter") {
        if (!draft.counter_outcome) {
          toast.error("Pick a counter-offer outcome");
          setBusy(false);
          return;
        }
        payload.counter_outcome = draft.counter_outcome;
        payload.counter_amount = Number(draft.counter_amount) || 0;
        if (draft.counter_outcome === "replacement") {
          payload.replacement_shipping_payer = draft.shipping_payer || "buyer";
          payload.replacement_shipping_fee =
            (draft.shipping_payer || "buyer") === "buyer"
              ? Number(draft.shipping_fee) || 0
              : 0;
        }
      }
      const headers = stepUpToken ? { "X-Step-Up-Token": stepUpToken } : {};
      const { data } = await api.post(`/claims/${claim.id}/reply`, payload, { headers });
      onChange(data);
      setDraft({ note: "", counter_outcome: "", counter_amount: "" });
      setPending([]);
      if (turn === "accept" && sellerSettlingRefund) {
        toast.success(`Refund of ${money(currentOfferAmount)} sent to buyer's wallet`);
      } else {
        toast.success(`Reply sent · ${turn}`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.code === "stepup_required") {
        setStepUp({ amount: detail.amount || currentOfferAmount });
        return; // don't clear busy; modal will clear on verified/close
      }
      const msg = (detail && typeof detail === "object" ? detail.message : detail) || "Reply failed";
      toast.error(msg);
    } finally {
      if (!stepUp) setBusy(false);
    }
  };

  return (
    <div className="mt-4 space-y-4 pb-8">
      {/* Header strip */}
      <div className="rounded-xl border border-border p-4 bg-foreground/[0.02]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs text-foreground/55">Order</div>
            <div className="text-sm font-medium">{claim.order_code} · {role === "buyer" ? claim.seller_name : claim.buyer_name}</div>
            <div className="text-[11px] text-foreground/55 mt-0.5">{REASON_LABEL[claim.reason] || claim.reason} · requesting {claim.requested_outcome} · {money(claim.requested_amount || 0)}</div>
          </div>
          {isActive && remain && (
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
              remain.expired ? "bg-foreground/10 text-foreground/55"
              : remain.urgent ? "bg-rose-100 text-rose-800"
              : "bg-amber-100 text-amber-800"
            }`}>
              <Hourglass className="w-3 h-3" />
              {remain.expired ? "Deadline passed" : `${remain.label} to deadline`}
            </span>
          )}
        </div>
      </div>

      {/* Status banner for terminal states */}
      {claim.status === "settled" && (
        <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50/50 p-4 flex items-start gap-3" data-testid="claim-settled-banner">
          <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-medium">Settled bilaterally</div>
            <div className="text-foreground/65 mt-0.5">
              {!claim.resolution?.outcome
                ? "Settled · outcome not recorded"
                : claim.resolution.outcome === "replacement"
                  ? "Replacement"
                  : `${claim.resolution.outcome === "partial_credit" ? "Partial refund" : "Full refund"} · ${money(claim.resolution?.amount || 0)}`}
              {" · settled "}{new Date(claim.resolution?.settled_at).toLocaleString()}
            </div>
            {claim.resolution?.refund_ref && (
              <div className="text-[11px] text-foreground/55 mono mt-1" data-testid="claim-refund-ref">
                Refund reference: {claim.resolution.refund_ref} · credited to buyer's wallet
              </div>
            )}
            {claim.resolution?.replacement_order_code && (
              <div className="mt-2 p-2 rounded-lg bg-white border border-emerald-200" data-testid="claim-replacement-banner">
                <div className="text-[11px] text-foreground/55 uppercase tracking-widest">Replacement order</div>
                <div className="text-sm font-medium mono mt-0.5">{claim.resolution.replacement_order_code}</div>
                {claim.resolution.shipping_payer === "buyer" && claim.resolution.shipping_fee > 0 ? (
                  <div className="text-[11px] text-foreground/60 mt-1">
                    Buyer pays {money(claim.resolution.shipping_fee)} shipping —
                    {claim.resolution.replacement_invoice_code
                      ? ` invoice ${claim.resolution.replacement_invoice_code}`
                      : " shipping invoice issued"}.
                    Replacement ships as soon as shipping is paid.
                  </div>
                ) : (
                  <div className="text-[11px] text-foreground/60 mt-1">
                    Seller covers shipping · replacement going into preparation now.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {claim.status === "auto_forwarded_allianz" && (
        <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50/50 p-4 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-medium">Auto-forwarded to Allianz</div>
            <div className="text-foreground/65 mt-0.5">
              Reference {claim.allianz_claim_ref || "—"} · the insurer's claims pipeline takes over from here.
            </div>
          </div>
        </div>
      )}
      {claim.status === "auto_closed_no_proof" && (
        <div className="rounded-xl border-2 border-rose-300 bg-rose-50/50 p-4 flex items-start gap-3">
          <TrendingDown className="w-5 h-5 text-rose-700 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-medium">Auto-closed · no proof</div>
            <div className="text-foreground/65 mt-0.5">
              Deadline expired without resolution. {role === "buyer" ? "Your verqo Trade Score dropped " : "Buyer's verqo Trade Score dropped "}
              {Math.abs(claim.score_impact || 0)} points.
            </div>
          </div>
        </div>
      )}

      {/* Quantity-mismatch lines (if relevant) */}
      {claim.qty_mismatch_lines?.length > 0 && (
        <div className="rounded-xl border border-border p-4">
          <div className="text-xs uppercase tracking-widest text-foreground/55 mb-2">Quantity reconciliation</div>
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase text-foreground/50">
              <tr><th className="text-left py-1">Item</th><th className="text-right py-1">Paid</th><th className="text-right py-1">Received</th><th className="text-right py-1">Δ</th></tr>
            </thead>
            <tbody>
              {claim.qty_mismatch_lines.map((ln, i) => {
                const diff = (ln.received_qty || 0) - (ln.paid_qty || 0);
                return (
                  <tr key={i} className="border-t border-border">
                    <td className="py-1.5">{ln.name}</td>
                    <td className="text-right">{ln.paid_qty}</td>
                    <td className="text-right">{ln.received_qty}</td>
                    <td className={`text-right font-medium ${diff < 0 ? "text-rose-700" : diff > 0 ? "text-emerald-700" : "text-foreground/40"}`}>
                      {diff === 0 ? "0" : diff > 0 ? `+${diff}` : `${diff}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Timeline */}
      <div className="rounded-xl border border-border bg-white">
        <div className="px-4 py-2.5 border-b border-border text-xs uppercase tracking-widest text-foreground/55">
          Bilateral timeline · {claim.events?.length || 0} turn{claim.events?.length === 1 ? "" : "s"}
        </div>
        <div className="p-4 space-y-3">
          {(claim.events || []).map(e => (
            <ClaimEvent key={e.id} event={e} youAre={youAre} />
          ))}
        </div>
      </div>

      {/* Reply form (only when claim is still active) */}
      {isActive && !remain?.expired && (
        <div className="rounded-xl border-2 border-foreground/15 bg-foreground/[0.02] p-4 space-y-3">
          <div className="text-xs uppercase tracking-widest text-foreground/55">Your reply</div>
          <textarea
            value={draft.note}
            onChange={e => setDraft(d => ({ ...d, note: e.target.value }))}
            placeholder="Add context for your reply… (optional)"
            rows={3}
            data-testid="claim-reply-note"
            className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/10"
          />
          <ProofUploader pending={pending} setPending={setPending} />
          <div className="grid grid-cols-2 gap-2">
            <select
              value={draft.counter_outcome}
              onChange={e => setDraft(d => ({ ...d, counter_outcome: e.target.value }))}
              className="rounded-lg border border-border px-3 py-2 text-sm"
              data-testid="claim-counter-outcome"
            >
              <option value="">Counter outcome (optional)</option>
              <option value="refund">Full refund</option>
              <option value="replacement">Replacement</option>
              <option value="partial_credit">Partial refund</option>
            </select>
            <input
              type="number"
              value={draft.counter_amount}
              onChange={e => setDraft(d => ({ ...d, counter_amount: e.target.value }))}
              placeholder="Counter amount $"
              className="rounded-lg border border-border px-3 py-2 text-sm"
              data-testid="claim-counter-amount"
            />
          </div>

          {draft.counter_outcome === "replacement" && (
            <div className="rounded-xl border-2 border-foreground/15 bg-white p-3 space-y-2" data-testid="counter-shipping-block">
              <div className="text-[11px] uppercase tracking-widest text-foreground/55">
                Replacement shipping
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "buyer",  l: "Buyer pays shipping" },
                  { v: "seller", l: "I'll pay shipping" },
                ].map(o => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setDraft(d => ({ ...d, shipping_payer: o.v }))}
                    data-testid={`counter-shipping-payer-${o.v}`}
                    className={`text-center rounded-lg border p-2 text-[12px] transition ${
                      draft.shipping_payer === o.v ? "border-foreground bg-accent/10" : "border-border hover:border-foreground/40"
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
              {draft.shipping_payer === "buyer" && (
                <input
                  type="number"
                  value={draft.shipping_fee}
                  onChange={e => setDraft(d => ({ ...d, shipping_fee: e.target.value }))}
                  placeholder="Shipping fee (USD) — buyer will be invoiced"
                  className="w-full rounded-lg border border-border px-3 py-2 text-sm"
                  data-testid="counter-shipping-fee"
                />
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={busy || offerByMyRole}
              onClick={() => reply("accept")}
              data-testid="claim-reply-accept"
              className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700 h-9 px-4 text-xs disabled:opacity-40"
              title={offerByMyRole ? "Waiting for counterparty — you can't accept your own offer" : undefined}
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
              {offerByMyRole
                ? "Awaiting counterparty"
                : sellerSettlingRefund
                  ? `Accept & refund ${money(currentOfferAmount)} to wallet`
                  : settlingReplacement
                    ? (currentShippingPayer === "buyer" && currentShippingFee > 0
                        ? `Accept · replacement (buyer pays ${money(currentShippingFee)} shipping)`
                        : `Accept · replacement (${currentShippingPayer === "seller" ? "seller" : "buyer"} pays shipping)`)
                    : "Accept current offer"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || !draft.counter_outcome}
              onClick={() => reply("counter")}
              data-testid="claim-reply-counter"
              className="rounded-full h-9 px-4 text-xs"
            >
              <DollarSign className="w-3.5 h-3.5 mr-1.5" />
              Send counter-offer
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => reply("add_proof")}
              data-testid="claim-reply-proof"
              className="rounded-full h-9 px-4 text-xs"
            >
              <Camera className="w-3.5 h-3.5 mr-1.5" />
              Add proof only
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => reply("reject")}
              data-testid="claim-reply-reject"
              className="rounded-full h-9 px-4 text-xs text-foreground/65"
            >
              <X className="w-3.5 h-3.5 mr-1.5" />
              Reject &amp; respond later
            </Button>
          </div>
        </div>
      )}

      {/* SMS step-up for seller when settling a refund outcome */}
      <StepUpModal
        open={!!stepUp}
        onClose={() => { setStepUp(null); setBusy(false); }}
        action="seller_refund"
        amount={stepUp?.amount}
        title={`Confirm refund of ${money(stepUp?.amount || 0)}`}
        description="For your security, every refund to a buyer's wallet requires a one-time SMS code."
        onVerified={(token) => { setStepUp(null); reply("accept", token); }}
      />
    </div>
  );
}

function ClaimEvent({ event, youAre }) {
  const isMine = event.by_role === youAre;
  const kindLabel = {
    open: "opened the claim",
    counter: "sent a counter-offer",
    accept: "accepted the offer",
    reject: "rejected the offer",
    add_proof: "added more proof",
  }[event.kind] || event.kind;
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-[13px] ${
        isMine ? "bg-foreground text-background rounded-tr-sm" : "bg-secondary/50 border border-border rounded-tl-sm"
      }`}>
        <div className={`text-[11px] mb-1 ${isMine ? "text-background/60" : "text-foreground/55"}`}>
          <span className="font-medium uppercase tracking-widest">{event.by_role}</span> · {kindLabel} · {new Date(event.at).toLocaleString()}
        </div>
        {event.note && <div className="leading-snug whitespace-pre-wrap">{event.note}</div>}
        {(event.outcome || event.amount != null) && (
          <div className={`mt-1 text-[11px] ${isMine ? "text-background/65" : "text-foreground/65"}`}>
            {event.outcome && <span className="uppercase tracking-widest">{event.outcome}</span>}
            {event.amount != null && event.amount !== 0 && <span> · {money(event.amount)}</span>}
          </div>
        )}
        {event.attachment_paths?.length > 0 && (
          <div className="mt-2 grid grid-cols-3 gap-1.5">
            {event.attachment_paths.map((p, i) => (
              <ProofThumb key={i} path={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProofThumb({ path }) {
  const isVideo = /\.(mp4|mov|webm)$/i.test(path);
  const token = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/claims/files/${path}?auth=${encodeURIComponent(token)}`;
  if (isVideo) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="aspect-square rounded bg-foreground/10 grid place-items-center text-foreground/55">
        <Video className="w-4 h-4" />
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="aspect-square rounded overflow-hidden bg-foreground/10">
      <img src={url} alt="proof" className="w-full h-full object-cover" />
    </a>
  );
}

// ─── Reusable proof uploader (used by both Open + Reply forms) ──────────────
function ProofUploader({ pending, setPending }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/claims/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPending(p => [...p, { path: data.path, content_type: data.content_type, name: f.name }]);
      } catch (err) {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,video/mp4,video/quicktime,video/webm"
        onChange={onPick}
        className="hidden"
        data-testid="claim-proof-input"
      />
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          data-testid="claim-proof-pick"
          className="rounded-full h-9 px-3 text-xs"
        >
          <Camera className="w-3.5 h-3.5 mr-1.5" />
          {busy ? "Uploading…" : "Add photos / video"}
        </Button>
        {pending.map((p, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-foreground/5 border border-border rounded-full px-2 py-1">
            {p.content_type?.startsWith("video") ? <Video className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
            <span className="truncate max-w-[140px]">{p.name}</span>
            <button
              type="button"
              onClick={() => setPending(pp => pp.filter((_, j) => j !== i))}
              className="text-foreground/40 hover:text-foreground"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Open-new-claim form (buyer only) ────────────────────────────────────────
function OpenClaimForm({ order, eligibleOrders, onCancel, onSubmitted }) {
  const [orderId, setOrderId] = useState(order.id);
  const [reason, setReason] = useState("damaged");
  const [outcome, setOutcome] = useState("refund");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [pending, setPending] = useState([]);
  const [qtyLines, setQtyLines] = useState(() =>
    (order.items || []).map(i => ({ sku: i.sku || "", name: i.name, paid_qty: i.qty, received_qty: i.qty }))
  );
  const [shippingPayer, setShippingPayer] = useState("buyer");
  const [busy, setBusy] = useState(false);

  // Buyer-arranged shipping blocks transit-damage claims (the seller's
  // release receipt is on file). Auto-switch reason away if the buyer
  // picks an order where 'damaged' is blocked.
  const activeOrder = eligibleOrders.find(e => e.id === orderId) || order;
  const buyerArranged = activeOrder?.shipping_mode === "buyer_arranged";
  useEffect(() => {
    if (buyerArranged && reason === "damaged") setReason("quality");
  }, [buyerArranged, reason]);

  // Reset quantity lines when buyer switches to a different order
  useEffect(() => {
    const o = eligibleOrders.find(e => e.id === orderId);
    if (o) {
      setQtyLines((o.items || []).map(i => ({ sku: i.sku || "", name: i.name, paid_qty: i.qty, received_qty: i.qty })));
    }
  }, [orderId, eligibleOrders]);

  const submit = async () => {
    if (pending.length === 0) {
      const ok = window.confirm("No proof attached. Without evidence your claim may auto-close and reduce your verqo Trade Score by 30-60 points. Submit anyway?");
      if (!ok) return;
    }
    setBusy(true);
    try {
      const payload = {
        reason,
        requested_outcome: outcome,
        requested_amount: Number(amount) || 0,
        note,
        attachment_paths: pending.map(p => p.path),
      };
      if (reason === "qty_mismatch") {
        payload.qty_mismatch_lines = qtyLines.map(l => ({
          sku: l.sku || null,
          name: l.name,
          paid_qty: Number(l.paid_qty) || 0,
          received_qty: Number(l.received_qty) || 0,
        }));
      }
      if (outcome === "replacement") {
        payload.replacement_shipping_payer = shippingPayer;
      }
      const { data } = await api.post(`/orders/${orderId}/claims`, payload);
      onSubmitted(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to open claim");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 space-y-4 pb-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-foreground/55 mb-2">Order</div>
        <select
          value={orderId}
          onChange={e => setOrderId(e.target.value)}
          data-testid="claim-form-order"
          className="w-full rounded-lg border border-border px-3 py-2 text-sm"
        >
          {eligibleOrders.map(o => (
            <option key={o.id} value={o.id}>{o.code} · {o.seller_name} · {money(o.total)}</option>
          ))}
        </select>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-foreground/55 mb-2">Reason</div>
        <div className="grid grid-cols-2 gap-2">
          {[
            { v: "damaged",      l: "Damaged in transit" },
            { v: "wrong_item",   l: "Wrong item received" },
            { v: "quality",      l: "Quality below spec" },
            { v: "qty_mismatch", l: "Quantity mismatch" },
            { v: "expired",      l: "Expired / near-expiry" },
            { v: "other",        l: "Other" },
          ].map(r => {
            const disabled = buyerArranged && r.v === "damaged";
            return (
              <button
                key={r.v}
                onClick={() => !disabled && setReason(r.v)}
                disabled={disabled}
                title={disabled ? "Transit damage isn't covered on buyer-arranged shipments — the seller's release receipt is on file." : undefined}
                data-testid={`claim-form-reason-${r.v}`}
                className={`text-left text-sm rounded-lg border p-3 transition ${
                  disabled ? "border-border bg-secondary/40 text-foreground/35 cursor-not-allowed" :
                  reason === r.v ? "border-foreground bg-accent/10" : "border-border hover:border-foreground/40"
                }`}
              >
                {r.l}
                {disabled && <div className="text-[10px] mono uppercase tracking-widest text-foreground/40 mt-1">Buyer-arranged · not covered</div>}
              </button>
            );
          })}
        </div>
      </div>

      {reason === "qty_mismatch" && (
        <div className="rounded-xl border-2 border-foreground/15 bg-accent/[0.06] p-4">
          <div className="text-xs uppercase tracking-widest text-foreground/55 mb-3">Quantity reconciliation</div>
          <div className="space-y-2">
            {qtyLines.map((l, i) => {
              const diff = (Number(l.received_qty) || 0) - (Number(l.paid_qty) || 0);
              return (
                <div key={i} className="grid grid-cols-12 gap-2 items-center text-sm">
                  <div className="col-span-6 truncate">{l.name}</div>
                  <div className="col-span-2 text-right text-foreground/55">Paid {l.paid_qty}</div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={l.received_qty}
                      onChange={e => setQtyLines(lines => lines.map((x, j) => j === i ? { ...x, received_qty: e.target.value } : x))}
                      className="w-full rounded border border-border px-2 py-1 text-right text-xs"
                    />
                  </div>
                  <div className={`col-span-2 text-right text-xs font-medium ${
                    diff < 0 ? "text-rose-700" : diff > 0 ? "text-emerald-700" : "text-foreground/40"
                  }`}>
                    {diff === 0 ? "OK" : diff > 0 ? `+${diff}` : `${diff}`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <div className="text-xs uppercase tracking-widest text-foreground/55 mb-2">Preferred resolution</div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { v: "refund",         i: RefreshCw, l: "Full refund",       d: "Wallet credit" },
            { v: "replacement",    i: Package,   l: "Replacement",       d: "Re-ship items" },
            { v: "partial_credit", i: DollarSign, l: "Partial refund",   d: "Wallet credit" },
          ].map(o => (
            <button
              key={o.v}
              onClick={() => setOutcome(o.v)}
              data-testid={`claim-form-outcome-${o.v}`}
              className={`text-left rounded-lg border p-3 transition ${
                outcome === o.v ? "border-foreground bg-accent/10" : "border-border hover:border-foreground/40"
              }`}
            >
              <o.i className="w-4 h-4 text-foreground/60 mb-1" />
              <div className="text-sm font-medium">{o.l}</div>
              <div className="text-[11px] text-foreground/55">{o.d}</div>
            </button>
          ))}
        </div>
      </div>

      {outcome === "replacement" && (
        <div className="rounded-xl border-2 border-foreground/15 bg-accent/[0.06] p-4" data-testid="claim-form-shipping-block">
          <div className="text-xs uppercase tracking-widest text-foreground/55 mb-2">Return shipping on replacement</div>
          <p className="text-[12px] text-foreground/60 mb-3">
            Who covers the cost of shipping the replacement to you? Sellers usually ask buyers to cover shipping on replacements; they can still counter-offer a different split.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: "buyer",  l: "I'll pay shipping",   d: "Default — faster approval" },
              { v: "seller", l: "Seller pays shipping", d: "Ask seller to cover it" },
            ].map(o => (
              <button
                key={o.v}
                onClick={() => setShippingPayer(o.v)}
                data-testid={`claim-form-shipping-${o.v}`}
                className={`text-left rounded-lg border p-3 transition ${
                  shippingPayer === o.v ? "border-foreground bg-white" : "border-border bg-white hover:border-foreground/40"
                }`}
              >
                <div className="text-sm font-medium">{o.l}</div>
                <div className="text-[11px] text-foreground/55">{o.d}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          value={amount}
          onChange={e => setAmount(e.target.value)}
          placeholder="Amount $"
          data-testid="claim-form-amount"
          className="rounded-lg border border-border px-3 py-2 text-sm"
        />
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Note (optional)"
          rows={1}
          data-testid="claim-form-note"
          className="rounded-lg border border-border px-3 py-2 text-sm"
        />
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-foreground/55 mb-2">Evidence</div>
        <ProofUploader pending={pending} setPending={setPending} />
        <div className="text-[11px] text-foreground/55 mt-2">
          ⚠ Without proof your claim may auto-close at deadline and reduce your verqo Trade Score by 30-60 points.
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <Button variant="ghost" onClick={onCancel} className="rounded-full h-11 px-5">Cancel</Button>
        <Button
          onClick={submit}
          disabled={busy}
          data-testid="claim-form-submit"
          className="flex-1 rounded-full h-11 bg-foreground text-background hover:bg-foreground/90"
        >
          <Send className="w-4 h-4 mr-1.5" />
          Submit claim
        </Button>
      </div>
    </div>
  );
}
